import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { toast } from "sonner";
import { PageHeader } from "~/components/page-header";
import { DataTable } from "~/components/data-table";
import { FilterTabs, type FilterTab } from "~/components/filter-tabs";
import { MasterDetail } from "~/components/master-detail";
import { SearchInput } from "~/components/search-input";
import { StatusBadge } from "~/components/status-badge";
import { ParcelDetailPanel } from "~/components/ops/parcel-detail-panel";
import { ParcelUnread } from "~/components/ops/unread";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { api, type OpsTransition } from "~/lib/api";
import { formatKES, formatDate, formatEta, etaIsLate, statusLabel } from "~/lib/format";
import { matchesParcel } from "~/lib/ops-search";
import { cn } from "~/lib/utils";
import type { IParcel } from "~/lib/proto";

const BULK_ACTIONS: Record<string, { label: string; action: OpsTransition }> = {
  REGISTERED: { label: "Mark received abroad", action: "receive" },
  OUT_FOR_DELIVERY: { label: "Mark delivered", action: "deliver" },
};

type Search = { status?: string; parcel?: string };

export const Route = createFileRoute("/_authed/ops/parcels")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    status: typeof search.status === "string" ? search.status : undefined,
    parcel: typeof search.parcel === "string" ? search.parcel : undefined,
  }),
  component: OpsParcels,
});

const STATUSES = ["REGISTERED", "RECEIVED_ABROAD", "RECEIVED_KENYA", "ON_HOLD", "PAID", "OUT_FOR_DELIVERY", "DELIVERED"];
const ALL = "ALL";

function relTime(iso?: string | null): string {
  if (!iso) return "";
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${formatDistanceToNowStrict(d)} ago`;
}

const columns: ColumnDef<IParcel, unknown>[] = [
  {
    header: "Customer",
    accessorKey: "customerName",
    cell: ({ row }) => {
      const p = row.original;
      return (
        <div className="flex max-w-[140px] min-w-0 items-center gap-2 @2xl:max-w-[190px]">
          <div className="min-w-0 leading-tight">
            <p className="truncate text-[13px] font-medium">{p.customerName || "No name"}</p>
            <p className="text-text-faint tabular truncate text-[11px]">{p.customerPhone}</p>
          </div>
          <ParcelUnread parcelId={p.id} />
        </div>
      );
    },
  },
  {
    header: "Parcel",
    accessorKey: "description",
    cell: ({ row }) => {
      const p = row.original;
      return (
        <div className="max-w-[120px] min-w-0 leading-tight @2xl:max-w-[190px]">
          <p className="truncate text-[13px]">{p.description || "Parcel"}</p>
          <p className="text-text-faint truncate text-[11px]" title={p.trackingNumber ?? undefined}>
            {p.trackingNumber || "No tracking number"}
          </p>
        </div>
      );
    },
    meta: { headClassName: "hidden @md:table-cell", cellClassName: "hidden @md:table-cell" },
  },
  {
    header: "Status",
    accessorKey: "status",
    cell: ({ row }) => {
      const p = row.original;
      const eta = formatEta(p.etaMinAt, p.etaMaxAt);
      const late = etaIsLate(p.etaMaxAt, p.status);
      return (
        <div className="leading-tight">
          <StatusBadge status={p.status} />
          {eta && p.status !== "DELIVERED" && (
            <p className={cn("mt-1 hidden text-[11px] whitespace-nowrap @xl:block", late ? "text-foreground font-medium" : "text-text-faint")}>
              {late ? "Overdue, was " : "Due "}
              {eta}
            </p>
          )}
        </div>
      );
    },
  },
  {
    header: "Amount",
    accessorKey: "amountChargedKes",
    cell: ({ row }) =>
      row.original.amountChargedKes ? (
        <span className="tabular text-[13px] whitespace-nowrap">{formatKES(row.original.amountChargedKes)}</span>
      ) : (
        <span className="text-text-faint text-[12px]">Not set</span>
      ),
    meta: { headClassName: "hidden text-right @2xl:table-cell", cellClassName: "hidden text-right @2xl:table-cell" },
  },
  {
    header: "Updated",
    accessorKey: "updatedAt",
    cell: ({ row }) => (
      <span className="text-text-faint text-[12px] whitespace-nowrap" title={formatDate(row.original.updatedAt)}>
        {relTime(row.original.updatedAt)}
      </span>
    ),
    meta: { headClassName: "hidden @3xl:table-cell", cellClassName: "hidden @3xl:table-cell" },
  },
];

function OpsParcels() {
  const { status, parcel: parcelParam } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(parcelParam ?? null);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (parcelParam) setSelected(parcelParam);
  }, [parcelParam]);

  const { data: parcels, isLoading, isError, refetch } = useQuery({
    queryKey: ["ops", "parcels", status ?? "all"],
    queryFn: () => api.ops.listParcels(status === "DELIVERED" ? { archived: true } : { status }),
  });

  const { data: allActive } = useQuery({
    queryKey: ["ops", "parcels", "counts"],
    queryFn: () => api.ops.listParcels({}),
  });
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const p of allActive ?? []) c[p.status ?? ""] = (c[p.status ?? ""] ?? 0) + 1;
    return c;
  }, [allActive]);

  const filtered = useMemo(() => (parcels ?? []).filter((p) => matchesParcel(p, query)), [parcels, query]);

  function setStatus(next: string) {
    setSelected(null);
    setSelectedIds(new Set());
    void navigate({ to: "/ops/parcels", search: next === ALL ? {} : { status: next } });
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const rows = filtered;
      if (rows.length === 0) return;
      if (e.key === "j" || e.key === "ArrowDown" || e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        const idx = selected ? rows.findIndex((p) => p.id === selected) : -1;
        const dir = e.key === "j" || e.key === "ArrowDown" ? 1 : -1;
        const next = idx < 0 ? (dir === 1 ? 0 : rows.length - 1) : Math.min(rows.length - 1, Math.max(0, idx + dir));
        setSelected(rows[next]?.id ?? null);
      } else if (e.key === "Escape") {
        setSelected(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtered, selected]);

  const selectedParcels = filtered.filter((p) => selectedIds.has(p.id!));
  const commonStatus =
    selectedParcels.length > 0 && selectedParcels.every((p) => p.status === selectedParcels[0].status)
      ? selectedParcels[0].status ?? ""
      : null;
  const bulk = commonStatus ? BULK_ACTIONS[commonStatus] : undefined;

  async function applyBulk() {
    if (!bulk) return;
    setBulkBusy(true);
    const ids = selectedParcels.map((p) => p.id!).filter(Boolean);
    const results = await Promise.allSettled(ids.map((id) => api.ops.transition(id, bulk.action)));
    const done = results.filter((r) => r.status === "fulfilled").length;
    setBulkBusy(false);
    setSelectedIds(new Set());
    void qc.invalidateQueries({ queryKey: ["ops", "parcels"] });
    if (done === ids.length) toast.success(`${done} ${done === 1 ? "parcel" : "parcels"} updated`);
    else toast.error(`${done} updated, ${ids.length - done} failed. Open the failed ones to see why.`);
  }

  function toggleId(id: string, on: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const allSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id!));
  const someSelected = filtered.some((p) => selectedIds.has(p.id!)) && !allSelected;

  const selectColumn: ColumnDef<IParcel, unknown> = {
    id: "select",
    header: () => (
      <Checkbox
        checked={allSelected ? true : someSelected ? "indeterminate" : false}
        onCheckedChange={(v) => setSelectedIds(v === true ? new Set(filtered.map((p) => p.id!).filter(Boolean)) : new Set())}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds.has(row.original.id!)}
        onCheckedChange={(v) => toggleId(row.original.id!, v === true)}
        onClick={(e) => e.stopPropagation()}
        aria-label="Select parcel"
      />
    ),
    meta: { headClassName: "w-[40px] max-md:hidden", cellClassName: "max-md:hidden" },
  };

  const tabs: FilterTab<string>[] = [
    { value: ALL, label: "Active", count: allActive?.length },
    ...STATUSES.map((s) => ({ value: s, label: statusLabel(s), count: counts[s] })),
  ];

  return (
    <>
      <PageHeader
        eyebrow="All parcels"
        title="Parcels"
        caption={parcels ? `${filtered.length} ${status ? statusLabel(status).toLowerCase() : "active"}` : undefined}
      />

      <FilterTabs label="Filter by stage" value={status ?? ALL} tabs={tabs} onChange={setStatus} />

      <SearchInput className="max-w-md" value={query} onChange={setQuery} placeholder="Name, phone, tracking number, customer code or ref" />

      {selectedIds.size > 0 && (
        <div className="border-hairline bg-muted/40 flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2">
          <span className="text-[13px] font-medium">{selectedParcels.length} selected</span>
          {bulk ? (
            <Button size="sm" loading={bulkBusy} onClick={() => void applyBulk()}>{bulk.label}</Button>
          ) : (
            <span className="text-text-faint text-[12px]">These are at different stages, so there is no single action for all of them.</span>
          )}
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      {isError ? (
        <div className="border-hairline bg-card rounded-lg border px-6 py-8 text-center">
          <p className="text-[14px] font-medium">Parcels did not load</p>
          <p className="text-text-faint mt-1 text-[13px]">This is a connection problem, not an empty list.</p>
          <Button variant="outline" className="mt-3" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <MasterDetail
          open={!!selected}
          onClose={() => setSelected(null)}
          detail={selected ? <ParcelDetailPanel parcelId={selected} /> : null}
          emptyHint="Select a parcel to manage it"
          title="Parcel"
        >
          <DataTable
            data={filtered}
            columns={[selectColumn, ...columns]}
            limit={filtered.length || 50}
            offset={0}
            onPageChange={() => {}}
            loading={isLoading}
            getRowId={(p) => p.id!}
            onRowClick={(p) => setSelected(p.id!)}
            selectedRowId={selected ?? undefined}
            emptyTitle={query.trim() ? "No matches" : "No parcels at this stage"}
            emptyBody={query.trim() ? "Check the number, or search every parcel from the bar at the top." : "Parcels appear here as they move."}
          />
        </MasterDetail>
      )}
    </>
  );
}
